import { useEffect, useRef, useState } from "react"

interface UseInViewOptions extends IntersectionObserverInit {
    /**
     * If true, the observer will disconnect after the element is first visible.
     * @default true
     */
    triggerOnce?: boolean
}

/**
 * A hook that uses IntersectionObserver to detect when an element is visible in the viewport.
 * Useful for lazy loading images and other content.
 *
 * @param options - IntersectionObserver options plus triggerOnce
 * @returns An object containing:
 *   - ref: A ref to attach to the element you want to observe
 *   - isInView: Whether the element is currently in view
 */
export default function useInView<T extends HTMLElement = HTMLDivElement>(options: UseInViewOptions = {}) {
    const { triggerOnce = true, threshold = 0.1, rootMargin = "100px", ...restOptions } = options

    const ref = useRef<T>(null)
    const [isInView, setIsInView] = useState(false)

    useEffect(() => {
        const element = ref.current
        if (!element) {
            return
        }

        // If already in view and triggerOnce, skip setting up observer
        if (isInView && triggerOnce) {
            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsInView(true)
                        if (triggerOnce) {
                            observer.disconnect()
                        }
                    } else if (!triggerOnce) {
                        setIsInView(false)
                    }
                })
            },
            { threshold, rootMargin, ...restOptions },
        )

        observer.observe(element)

        return () => {
            observer.disconnect()
        }
    }, [threshold, rootMargin, triggerOnce, isInView])

    return { ref, isInView }
}
